/*
 * Copyright (c) 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import privacyStyle from "@/src/components/Privacy/privacyStyle";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import Container from "@/src/components/Container/Container";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import { Helmet } from "react-helmet-async";
import MuiListNativeStyled from "@/src/components/List/MuiListNativeStyled";

const A11yStatementPage = () => {
  return (
    <LayoutPublicRoutes>
      <Helmet>
        <title>Barrierefreiheit bei Legalbird | Legalbird</title>
        <meta
          name="description"
          content="Informationen zur Barrierefreiheit der digitalen Dienstleistungen von Legalbird sowie Kontaktmöglichkeiten für Hinweise zu Barrieren."
        />
      </Helmet>
      <Container showVerticalPadding={false}>
        <Box sx={privacyStyle.privacyPage} id="barrierefreiheit">
          <Typography variant={"h1"} sx={privacyStyle.headline}>
            Informationen zur Barrierefreiheit
          </Typography>
          <Typography>
            Diese Informationen gelten für die über legalbird.de angebotene digitale Servicewelt der Legalbird GmbH und die über legalbird.de angebotenen
            anwaltlichen Dienstleistungen der Legalbird Rechtsanwälte Rechtsanwalts GmbH.
          </Typography>
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            Anbieter der Dienstleistungen
          </Typography>
          <Typography>Die digitale Servicewelt („Legalbird OS“) wird angeboten von:</Typography>
          <Typography component={"address"} sx={{ marginBottom: "1rem", fontStyle: "normal" }}>
            Legalbird GmbH <br />
            Im Zollhafen 24 <br />
            50678 Köln
          </Typography>
          <Typography>Die anwaltliche Prüfung, Beratung und Vertretung wird angeboten von: </Typography>
          <Typography component={"address"} sx={{ marginBottom: "1rem", fontStyle: "normal" }}>
            Legalbird Rechtsanwälte Rechtsanwalts GmbH <br />
            Im Zollhafen 24 <br />
            50678 Köln
          </Typography>
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            Beschreibung unserer Dienstleistungen
          </Typography>
          <Typography>
            Über legalbird.de können Verbraucherinnen und Verbraucher rechtliche Anliegen digital erfassen und anwaltliche Unterstützung anfragen. In der
            persönlichen Servicewelt können Falldaten und Dokumente bereitgestellt, der Bearbeitungsstand eingesehen und Nachrichten ausgetauscht werden. Die
            digitale Servicewelt wird von der Legalbird GmbH bereitgestellt. Die anwaltliche Prüfung, Beratung und Vertretung erfolgt durch die Legalbird
            Rechtsanwälte Rechtsanwalts GmbH.
          </Typography>
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            So funktioniert die Dienstleistung
          </Typography>
          <MuiListNativeStyled
            sx={{ margin: "1rem 0", listStyleType: "decimal" }}
            listItems={[
              "Die Nutzerin oder der Nutzer wählt ein Rechtsgebiet beziehungsweise ein rechtliches Anliegen aus.",
              "Ein digitaler Fragenprozess erfasst die für die Bearbeitung erforderlichen Angaben. Für die Nutzung ist eine Registrierung und die Anlage eines persönlichen Benutzerkontos erforderlich.",
              "Unterlagen können digital hochgeladen und Nachrichten über die persönliche Servicewelt ausgetauscht werden.",
              "Die Angaben werden geprüft. Anschließend werden die nächsten Schritte und – soweit beauftragt – die anwaltliche Beratung oder Vertretung über die Servicewelt und die angegebenen Kontaktwege kommuniziert.",
            ]}
          />
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            Barrierefreiheit unserer digitalen Dienstleistungen
          </Typography>
          <Typography>
            Bei der Gestaltung und Weiterentwicklung von legalbird.de und der persönlichen Servicewelt berücksichtigen wir die Anforderungen des
            Barrierefreiheitsstärkungsgesetzes (BFSG) und der zugehörigen Verordnung (BFSGV). Als technische Orientierung dienen uns die europäische Norm EN 301
            549 und die Web Content Accessibility Guidelines (WCAG) 2.1 auf Konformitätsstufe AA.
          </Typography>
          <Typography>Zu den berücksichtigten Bereichen gehören insbesondere:</Typography>
          <MuiListNativeStyled
            sx={{ margin: "1rem 0" }}
            listItems={[
              "die Bedienbarkeit mit der Tastatur und mit unterstützenden Technologien,",
              "nachvollziehbare Seiten-, Überschriften- und Formularstrukturen,",
              "verständliche Beschriftungen, Hinweise und Fehlermeldungen,",
              "ausreichende Kontraste sowie die Nutzbarkeit bei Vergrößerung und auf kleinen Bildschirmen,",
              "geeignete Textalternativen für nicht-textliche Inhalte und",
              "eine möglichst einheitliche und nachvollziehbare Navigation.",
            ]}
          />
          <Typography>
            Die Barrierefreiheit unserer digitalen Angebote wird fortlaufend überprüft und weiterentwickelt. Trotz dieser Maßnahmen können einzelne Barrieren
            bestehen. Wenn Sie bei der Nutzung auf eine Barriere stoßen, freuen wir uns über Ihre Rückmeldung.
          </Typography>
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            Unterstützung und Rückmeldung zu Barrieren
          </Typography>
          <Typography>
            Wenn Sie bei der Nutzung von legalbird.de auf eine Barriere stoßen oder Informationen in einer für Sie zugänglichen Form benötigen, kontaktieren Sie
            uns bitte:
          </Typography>
          <MuiListNativeStyled sx={{ margin: "1rem 0" }} listItems={["E-Mail: service@legalbird.de", "Telefon: 0221 98651940"]} />
          <Typography>
            Bitte beschreiben Sie möglichst genau, an welcher Stelle die Barriere aufgetreten ist. Wir prüfen Ihre Rückmeldung und stellen Ihnen die benötigten
            Informationen auf Wunsch über einen zugänglichen Kommunikationsweg bereit.
          </Typography>
          <Typography variant={"h2"} sx={privacyStyle.headline}>
            Zuständige Marktüberwachungsbehörde
          </Typography>
          <Typography>Marktüberwachungsstelle der Länder für die Barrierefreiheit von Produkten und Dienstleistungen (MLBF AöR)</Typography>
          <Typography component={"address"} sx={{ marginBottom: 0, fontStyle: "normal" }}>
            Carl-Miller-Straße 6 <br />
            39112 Magdeburg
          </Typography>
          <Typography>
            Telefon: +49 391 289 230 23 <br />
            E-Mail: kontakt@mlbf-barrierefrei.de <br />
            Webseite: https://www.mlbf-barrierefrei.de/
          </Typography>
          <Typography sx={{ marginTop: "2rem" }}>Stand: 21. September 2026</Typography>
        </Box>
      </Container>
    </LayoutPublicRoutes>
  );
};

export default A11yStatementPage;
